// Question Key Mapping for Backward Compatibility
// This ensures that user answers remain correctly mapped even if question text, order, or keys change

import { resolveMessage, t } from '../i18n/translate';

export interface QuestionMapping {
  id: string;        // Stable identifier (q1, q2, q3, etc.)
  key: string;       // Current question key
  labelKey: string;  // i18n catalog key holding the question text
  label: string;     // Question text in the active locale (resolves labelKey)
  required?: boolean; // Whether this question is required for completion
}

export interface MappedAnswer {
  key: string;       // Original question key
  answer: string;    // User's answer
}

export interface QuestionData {
  [questionId: string]: MappedAnswer; // e.g., { "q1": { key: "spirituality_childhood", answer: "..." } }
}

// Step 6 character defects use their own structure and are already encrypted

/** Question text lives in the i18n catalogs; `label` resolves in the active locale. */
function localizedQuestion(
  stepNumber: number,
  id: string,
  key: string,
  required: boolean
): QuestionMapping {
  const labelKey = `step.${stepNumber}.question.${id}.label`;
  return {
    id,
    key,
    labelKey,
    get label() {
      return t(labelKey);
    },
    required,
  };
}

/** Example-answer placeholder for a question, falling back to the shared default. */
export function getQuestionPlaceholder(stepNumber: number, questionId: string): string {
  const key = `step.${stepNumber}.question.${questionId}.placeholder`;
  const resolved = resolveMessage(key);
  return resolved === key ? t('step.question.placeholder_default') : resolved;
}

// Step 2 Question Mappings
export const STEP2_QUESTIONS: QuestionMapping[] = [
  localizedQuestion(2, "q1", "spirituality_childhood", true),
  localizedQuestion(2, "q2", "spirituality_now", true),
  localizedQuestion(2, "q3", "higher_power", true),
  localizedQuestion(2, "q4", "anger_higher_power", true),
  localizedQuestion(2, "q5", "prayer_deals", true),
  localizedQuestion(2, "q6", "authority_abuse", false)
];

// Step 3 Question Mappings
export const STEP3_QUESTIONS: QuestionMapping[] = [
  // Group: Decision
  localizedQuestion(3, "q1", "decision_maker_while_acting_out", true),
  localizedQuestion(3, "q2", "fears_about_decision", true),
  // Group: Self-will
  localizedQuestion(3, "q3", "problems_tried_to_resolve", true),
  localizedQuestion(3, "q4", "self_will_suffering", true),
  // Group: God of my understanding
  localizedQuestion(3, "q5", "decision_maker_in_recovery", true),
  localizedQuestion(3, "q6", "concept_of_higher_power", true),
  // Group: Turning it over
  localizedQuestion(3, "q7", "examples_turned_over_to_addiction", true),
  localizedQuestion(3, "q8", "day_if_turned_over", true),
  localizedQuestion(3, "q9", "thoughts_behaviors_willing_to_turn_over", true)
];

// Step 6 uses character defects which have a different structure
// Character defects are already encrypted and don't need question key mapping

// Migration mapping for old data format
export const OLD_TO_NEW_MAPPING = {
  step2: {
    "spirituality_childhood": "q1",
    "spirituality_now": "q2",
    "higher_power": "q3",
    "anger_higher_power": "q4",
    "prayer_deals": "q5",
    "authority_abuse": "q6"
  },
  step3: {
    "decision_maker_while_acting_out": "q1",
    "fears_about_decision": "q2",
    "problems_tried_to_resolve": "q3",
    "self_will_suffering": "q4",
    "decision_maker_in_recovery": "q5",
    "concept_of_higher_power": "q6",
    "examples_turned_over_to_addiction": "q7",
    "day_if_turned_over": "q8",
    "thoughts_behaviors_willing_to_turn_over": "q9"
  },
  // Step 6 character defects don't need migration mapping
};

/**
 * Migrate old question data to new mapped format
 */
export function migrateQuestionData(stepNumber: number, oldData: any): QuestionData {
  if (!oldData) return {};

  const stepKey = `step${stepNumber}` as keyof typeof OLD_TO_NEW_MAPPING;
  const mapping = OLD_TO_NEW_MAPPING[stepKey];

  if (!mapping) return oldData; // No migration needed

  const newData: QuestionData = {};

  Object.entries(oldData).forEach(([key, answer]) => {
    const newId = mapping[key as keyof typeof mapping];
    if (newId && typeof answer === 'string') {
      newData[newId] = { key, answer };
    }
  });

  return newData;
}

// Character defect migration functions removed - Step 6 uses original format

/**
 * Convert new mapped data back to simple key-value format for display
 */
export function unmappedQuestionData(mappedData: QuestionData): Record<string, string> {
  const result: Record<string, string> = {};
  
  Object.entries(mappedData).forEach(([questionId, mappedAnswer]) => {
    result[mappedAnswer.key] = mappedAnswer.answer;
  });
  
  return result;
}

/**
 * Get question mapping for a specific step
 */
export function getQuestionMapping(stepNumber: number): QuestionMapping[] {
  switch (stepNumber) {
    case 2:
      return STEP2_QUESTIONS;
    case 3:
      return STEP3_QUESTIONS;
    // Step 6 character defects don't use question mapping
    default:
      return [];
  }
}

/**
 * Get question by ID
 */
export function getQuestionById(stepNumber: number, questionId: string): QuestionMapping | undefined {
  const questions = getQuestionMapping(stepNumber);
  return questions.find(q => q.id === questionId);
}

/**
 * Get question by key
 */
export function getQuestionByKey(stepNumber: number, key: string): QuestionMapping | undefined {
  const questions = getQuestionMapping(stepNumber);
  return questions.find(q => q.key === key);
}

/**
 * Check if data needs migration
 */
export function needsMigration(data: any): boolean {
  if (!data) return false;
  
  // Check if data is in old format (simple key-value pairs)
  const firstValue = Object.values(data)[0];
  return typeof firstValue === 'string';
}

/**
 * Validate that all required questions are answered
 */
export function validateRequiredQuestions(stepNumber: number, data: QuestionData): boolean {
  const questions = getQuestionMapping(stepNumber);
  const requiredQuestions = questions.filter(q => q.required);
  
  return requiredQuestions.every(question => {
    const answer = data[question.id];
    return answer && answer.answer && answer.answer.trim().length > 0;
  });
} 